import ApiClient from "../helpers/ApiClient";
import useSWR from "swr";

const fetcher = (url) => ApiClient.get(url).then((res) => res.data);

/**
 * Fetch eligible inventory items with their base units, alternate units,
 * conversion factors, active custody, and real-time available stock.
 */
export function useEligibleItems() {
  const APIURL = "/item-issues/eligible-items";
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Fetch active shifts / daybook shifts for issue logging.
 */
export function useActiveShifts() {
  const APIURL = "/item-issues/shifts";
  const { data, error, isLoading } = useSWR(APIURL, fetcher);
  return {
    shifts: Array.isArray(data?.shifts) ? data.shifts : [],
    error,
    isLoading,
    APIURL,
  };
}

/**
 * Fetch Item Issue & Custody Dashboard statistics.
 */
export function useItemIssueDashboard() {
  const APIURL = "/item-issues/dashboard-stats";
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher, {
    refreshInterval: 10000,
  });
  return {
    stats: data?.stats || null,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Fetch Custody Register entries with active custody balances,
 * pending sales, and expected returnable quantities.
 */
export function useCustodyRegister({ user_id = "", department = "", status = "active" } = {}) {
  const params = new URLSearchParams();
  if (user_id) params.append("user_id", user_id);
  if (department) params.append("department", department);
  if (status && status !== "all") params.append("status", status);

  const queryStr = params.toString();
  const APIURL = `/item-issues/custody-register${queryStr ? `?${queryStr}` : ""}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    register: Array.isArray(data?.register) ? data.register : [],
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Fetch audit movements ledger.
 */
export function useMovements({
  search = "",
  movement_type = "all",
  user_id = "",
  inventory_item_id = "",
  from = "",
  to = "",
  page = 1,
  limit = 25,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (movement_type && movement_type !== "all") params.append("movement_type", movement_type);
  if (user_id) params.append("user_id", user_id);
  if (inventory_item_id) params.append("inventory_item_id", inventory_item_id);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  params.append("page", page);
  params.append("limit", limit);

  const APIURL = `/item-issues/movements?${params.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    movements: Array.isArray(data?.movements) ? data.movements : [],
    total: typeof data?.total === "number" ? data.total : 0,
    page: data?.page || 1,
    limit: data?.limit || limit,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Fetch Item Issue reports by product or custodian.
 */
export function useItemIssueReports({ from = "", to = "", type = "product" } = {}) {
  const params = new URLSearchParams();
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  if (type) params.append("type", type);

  const APIURL = `/item-issues/reports?${params.toString()}`;
  const { data, error, isLoading, mutate } = useSWR(APIURL, fetcher);

  return {
    reports: Array.isArray(data?.reports) ? data.reports : [],
    summary: data?.summary || null,
    error,
    isLoading,
    mutate,
    APIURL,
  };
}

/**
 * Issue items to staff/department.
 */
export async function issueItems(payload) {
  return await ApiClient.post("/item-issues/issue", payload);
}

/**
 * Return items from custody back to store.
 */
export async function returnItems(payload) {
  return await ApiClient.post("/item-issues/return", payload);
}

/**
 * Record non-sale internal consumption (breakage, wastage, cleaning, staff meal).
 */
export async function recordConsumption(payload) {
  return await ApiClient.post("/item-issues/consumption", payload);
}

/**
 * Create physical count reconciliation request (shortage/surplus).
 */
export async function createReconciliation(payload) {
  return await ApiClient.post("/item-issues/reconciliation", payload);
}

/**
 * Unified Settle & Reconcile:
 * Return items, record non-sale wastage/consumption, and audit physical count in one atomic flow.
 */
export async function settleAndReconcileCustody(payload) {
  return await ApiClient.post("/item-issues/settle-reconcile", payload);
}

/**
 * Approve pending reconciliation and post adjustment movements.
 */
export async function approveReconciliation(id, payload = {}) {
  return await ApiClient.post(`/item-issues/reconciliation/${id}/approve`, payload);
}
